<template>
    <div class="payment-methods-report-content">
        <div>
            <div style="display: flex; column-gap: 1em">
                <div class="input-container">
                    <div class="p-float-label">
                        <DatePicker
                            inputId="start-date-input"
                            v-model="startDate"
                            :min-date="minDate"
                            :max-date="maxDate"
                            :input-class="{ invalid: startDateInvalid }"
                            showIcon
                            fluid
                            iconDisplay="input"
                            dateFormat="dd.mm.yy"
                        />
                        <label for="start-date-input">{{ translations.general.start }}</label>
                    </div>
                </div>
                <div class="input-container">
                    <DatePicker
                        v-model="startDate"
                        input-class="input-time"
                        :min-date="minDate"
                        :max-date="maxDate"
                        showIcon
                        iconDisplay="input"
                        time-only
                    />
                </div>
            </div>
        </div>

        <div style="margin-top: 1em">
            <div style="display: flex; column-gap: 1em">
                <div class="input-container">
                    <div class="p-float-label" >
                        <DatePicker
                            :input-class="{ invalid: startDateInvalid }"
                            v-model="endDate"
                            :min-date="minDate"
                            :max-date="maxDate"
                            showIcon
                            fluid
                            iconDisplay="input"
                            dateFormat="dd.mm.yy"
                        />
                        <label for="end-date-input">{{ translations.general.end }}</label>
                    </div>
                </div>
                <div class="input-container" >
                    <DatePicker
                        input-class="input-time"
                        v-model="endDate"
                        :min-date="minDate"
                        :max-date="maxDate"
                        showIcon
                        iconDisplay="input"
                        time-only
                    />
                </div>
            </div>
        </div>
        <VButton
            @click="displayPaymentMethodsReport"
            :disabled="isLoading"
            class="show-report-btn primary rounded"
            style="margin: 1em 0"
        >
            {{ translations.general.display }}
        </VButton>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import type { IGeneratePaymentMethodsReportDataCmd } from "@/data/commands/order/GenerateSalesReportDataCmd";
    import { Factory } from "@/data/dao/Factory";
    import type { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { OrderDao } from "@/data/dao/service/OrderDao";
    import type { IPaymentMethodsReportData } from "@/data/model/PaymentMethodsReportData";
    import { useIdentityStore } from "@/data/stores/identity";
    import { usePaymentStore } from "@/data/stores/payment";
    import { exceptionTypes } from "@/data/util/Config";
    import { clientLanguage } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, onBeforeMount, ref } from "vue";

    import VButton from "@/components/elements/VButton.vue";
    import DatePicker from "primevue/calendar";

    const props = defineProps<{
        onExportPaymentMethod: (data: IPaymentMethodsReportData) => void;
    }>();

    const startDateInvalid = ref<boolean>(false);
    const endDateInvalid = ref<boolean>(false);
    const startDate = ref<Date | null>(new Date(new Date().setHours(0, 0, 0)));
    const endDate = ref<Date | null>(new Date(new Date()));
    const isLoading = ref<boolean>(false);

    const minDate = computed(() => {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 3);
        startDate.setHours(0, 0, 0, 0);
        return startDate;
    });

    const maxDate = computed(() => {
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);
        return endOfDay;
    });

    const orderDao = Factory.getInstance<OrderDao>(OrderDao);
    const payment = usePaymentStore();
    const identity = useIdentityStore();

    let abortController: AbortController | null = null;

    onBeforeMount(async () => {
        abortController?.abort();
    });

    async function displayPaymentMethodsReport(): Promise<void> {
        let isError = false;
        startDateInvalid.value = false;

        let selectedStartDate = startDate.value;
        let selectedEndDate = endDate.value;

        if (selectedStartDate == null) {
            isError = true;
            startDateInvalid.value = true;
        }

        if (selectedEndDate == null) {
            isError = true;
            endDateInvalid.value = true;
        }

        if (!isError) {
            const data: IGeneratePaymentMethodsReportDataCmd = {
                fulfillmentGroupIds: [],
                offerIds: [],
                roomIds: [],
                startDateTime: selectedStartDate!,
                endDateTime: selectedEndDate!,
                filterByEndUsers: false,
                hideZeroQtyPaymentMethods: true,
                waiterId: identity.identityId,
                language: clientLanguage(),
            };

            try {
                isLoading.value = true;

                abortController = new AbortController();
                const reportData = await orderDao.generatePaymentMethodsReportData(data, abortController.signal);
                await payment.setLastPaymentMethodsData(reportData);
                props.onExportPaymentMethod(reportData);
            } catch (ex) {
                logError(ex);

                const error = ex as DaoError;

                if (error.type === exceptionTypes.unauthorizedAccessToNonPortalReportException) {
                    Popup.show({ message: translations.report.noAccessToReports });
                } else {
                    Popup.show({ message: translations.general.operationFailed });
                }
            } finally {
                isLoading.value = false;
            }
        }
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .show-report-btn.v-btn.primary {
        font-size: 17px;
        margin-top: 0.625em;
        height: 2.75em;
        line-height: 2.75em;
        padding: 0 2em;
        border-radius: 1.5em !important;
        width: 100%;
    }

    .payment-methods-report-content {
        input.p-inputtext.p-component.invalid {
            border-color: colors.$red;
        }

        .input-container {
            padding-top: 24px;
        }

        input {
            &.input-time {
                width: 8rem !important;
            }

            width: 9rem;
        }
    }
</style>
